import React, { useMemo, useState } from 'react';
import { Button } from '@vehix/portal/components/ui/button';
import { formatInZone, getRunsInWindow, tzAbbrev, type NextRun } from '../../utils/cronNextRun';
import type { ScheduleRow } from '../../utils/scheduleRows';

interface PipelineSchedulesTimelineProps {
  rows: ScheduleRow[];
  now: Date;
  viewerZone: string;
  /** Opens the detail panel for a lane. */
  onSelect: (row: ScheduleRow) => void;
}

type Span = '24h' | '7d';

const HOUR_MS = 3_600_000;
const SPAN_MS: Record<Span, number> = { '24h': 24 * HOUR_MS, '7d': 7 * 24 * HOUR_MS };
const TICK_HOURS: Record<Span, number> = { '24h': 3, '7d': 24 };
const LANE_CAP = 40;
/** Bins for the density ribbon: 30 min at 24h, 3h at 7d. */
const BIN_MS: Record<Span, number> = { '24h': 30 * 60_000, '7d': 3 * HOUR_MS };

interface Lane {
  row: ScheduleRow;
  runs: NextRun[];
  truncated: boolean;
  /** Null when the cron or zone can't be read at all. */
  predictable: boolean;
}

/** Occurrences within this many percent of each other collapse into one marker. */
const CLUSTER_PCT = 0.8;

interface Cluster {
  pct: number;
  runs: NextRun[];
}

function clusterRuns(runs: NextRun[], start: number, spanMs: number): Cluster[] {
  const clusters: Cluster[] = [];
  for (const run of runs) {
    const pct = ((run.instant.getTime() - start) / spanMs) * 100;
    const last = clusters[clusters.length - 1];
    if (last && pct - last.pct < CLUSTER_PCT) last.runs.push(run);
    else clusters.push({ pct, runs: [run] });
  }
  return clusters;
}

const NO_PREDICT_CLASS =
  'absolute inset-0 flex items-center justify-center text-[10px] text-muted-foreground';
const CONTINUOUS_CLASS =
  'absolute inset-y-1 left-0 right-0 flex items-center justify-center rounded ' +
  'bg-primary/20 text-[10px]';
const MARKER_ON = 'bg-primary';
const MARKER_OFF = 'border border-dashed border-muted-foreground bg-transparent';

interface TimelineLaneProps {
  lane: Lane;
  start: number;
  spanMs: number;
  nowPct: number;
  onSelect: (row: ScheduleRow) => void;
}

// eslint-disable-next-line max-lines-per-function
const TimelineLane: React.FC<TimelineLaneProps> = ({ lane, start, spanMs, nowPct, onSelect }) => {
  const s = lane.row.schedule;
  const clusters = clusterRuns(lane.runs, start, spanMs);
  const tenantSuffix = s.tenants ? ` · ${s.tenants.tenant_name}` : '';

  return (
    <div
      className={
        'flex items-center border-b last:border-0 hover:bg-muted/40 ' +
        (s.is_enabled ? '' : 'opacity-40')
      }
    >
      <button
        type='button'
        onClick={() => onSelect(lane.row)}
        className='w-24 shrink-0 px-2 py-1.5 text-left sm:w-48'
        title={`${s.schedule_name}${tenantSuffix}`}
      >
        <p className='truncate text-xs font-medium'>{s.schedule_name}</p>
        {s.tenants && (
          <p className='hidden truncate text-[10px] text-muted-foreground sm:block'>
            {s.tenants.tenant_name}
          </p>
        )}
      </button>

      <div className='relative h-8 flex-1 bg-muted/20'>
        {!lane.predictable && <span className={NO_PREDICT_CLASS}>can&apos;t predict</span>}
        {lane.truncated && <span className={CONTINUOUS_CLASS}>runs continuously</span>}
        {!lane.truncated &&
          clusters.map(cluster => {
            const first = cluster.runs[0];
            if (!first) return null;
            const label = cluster.runs
              .map(
                r =>
                  `${formatInZone(r.instant, s.time_zone, 'ddd h:mm A')} ` +
                  tzAbbrev(r.instant, s.time_zone)
              )
              .join('\n');
            return (
              <button
                key={first.instant.toISOString()}
                type='button'
                onClick={() => onSelect(lane.row)}
                className='absolute top-1/2 h-5 w-3 -translate-x-1/2 -translate-y-1/2'
                style={{ left: `${cluster.pct}%` }}
                title={`${s.schedule_name}\n${label}`}
              >
                <span
                  className={
                    'mx-auto block h-5 w-[3px] rounded-sm ' +
                    (s.is_enabled ? MARKER_ON : MARKER_OFF)
                  }
                />
                {cluster.runs.length > 1 && (
                  <span className='absolute -right-1 -top-1 text-[8px] font-bold text-primary'>
                    {cluster.runs.length}
                  </span>
                )}
              </button>
            );
          })}
        {nowPct >= 0 && nowPct <= 100 && (
          <div
            className='pointer-events-none absolute inset-y-0 w-px bg-destructive'
            style={{ left: `${nowPct}%` }}
          />
        )}
      </div>
    </div>
  );
};

// eslint-disable-next-line max-lines-per-function
export const PipelineSchedulesTimeline: React.FC<PipelineSchedulesTimelineProps> = ({
  rows,
  now,
  viewerZone,
  onSelect
}) => {
  const [span, setSpan] = useState<Span>('24h');

  // A fixed duration from the top of the current hour — never a calendar day.
  // A day in a DST zone is 23 or 25 hours, so anchoring to one would shear
  // every marker on a transition day.
  const spanMs = SPAN_MS[span];
  const start = Math.floor(now.getTime() / HOUR_MS) * HOUR_MS;
  const end = start + spanMs;

  const lanes: Lane[] = useMemo(
    () =>
      rows.slice(0, LANE_CAP).map(row => {
        const win = getRunsInWindow(
          row.schedule.cron_expression,
          row.schedule.time_zone,
          new Date(start),
          new Date(end),
          span === '24h' ? 48 : 168
        );
        return {
          row,
          runs: win?.runs ?? [],
          truncated: win?.truncated ?? false,
          predictable: win !== null
        };
      }),
    [rows, start, end, span]
  );

  const ticks = useMemo(() => {
    const step = TICK_HOURS[span] * HOUR_MS;
    const out: Array<{ pct: number; label: string }> = [];
    for (let t = start; t < end; t += step) {
      out.push({
        pct: ((t - start) / spanMs) * 100,
        // Formatting a real instant, so a fall-back day correctly shows 1 AM
        // twice and a spring-forward day skips 2 AM.
        label: formatInZone(new Date(t), viewerZone, span === '24h' ? 'h A' : 'ddd')
      });
    }
    return out;
  }, [start, end, spanMs, span, viewerZone]);

  // Density across every visible lane — this is what makes "four tenants all
  // crawl at 2 AM" a single unmissable spike instead of four separate rows.
  const bins = useMemo(() => {
    const binMs = BIN_MS[span];
    const count = Math.ceil(spanMs / binMs);
    const buckets = new Array<number>(count).fill(0);
    for (const lane of lanes) {
      if (!lane.row.schedule.is_enabled) continue;
      for (const run of lane.runs) {
        const idx = Math.floor((run.instant.getTime() - start) / binMs);
        if (idx >= 0 && idx < count) buckets[idx] = (buckets[idx] ?? 0) + 1;
      }
    }
    const max = Math.max(1, ...buckets);
    // Keyed by the bin's own start instant rather than its index, so the list
    // stays stable when the window scrolls forward on the minute tick.
    return {
      bins: buckets.map((count, i) => ({ count, at: start + i * binMs })),
      max,
      widthPct: 100 / count
    };
  }, [lanes, start, spanMs, span]);

  const nowPct = ((now.getTime() - start) / spanMs) * 100;
  const totalRuns = lanes.reduce((n, l) => n + (l.row.schedule.is_enabled ? l.runs.length : 0), 0);

  return (
    <div className='space-y-3'>
      <div className='flex flex-wrap items-center gap-3'>
        <div className='flex'>
          {(['24h', '7d'] as const).map(s => (
            <Button
              key={s}
              type='button'
              variant='ghost'
              size='sm'
              aria-pressed={span === s}
              className={
                'h-8 rounded-none border-y border-r px-3 text-xs first:rounded-l-md ' +
                'first:border-l last:rounded-r-md ' +
                (span === s ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted')
              }
              onClick={() => setSpan(s)}
            >
              {s === '24h' ? 'Next 24 hours' : 'Next 7 days'}
            </Button>
          ))}
        </div>
        <span className='text-xs text-muted-foreground'>
          {totalRuns} run{totalRuns === 1 ? '' : 's'} scheduled · times shown in your timezone (
          {tzAbbrev(now, viewerZone)})
        </span>
      </div>

      <div className='rounded-md border'>
        {/* Axis + density ribbon */}
        <div className='flex border-b bg-background'>
          <div className='w-24 shrink-0 sm:w-48' />
          <div className='relative flex-1'>
            <div className='relative h-6'>
              {ticks.map(tick => (
                <span
                  key={tick.pct}
                  className='absolute top-1 text-[10px] text-muted-foreground'
                  style={{ left: `${tick.pct}%` }}
                >
                  {tick.label}
                </span>
              ))}
            </div>
            <div className='relative flex h-8 items-end border-t'>
              {bins.bins.map(bin => (
                <div
                  key={bin.at}
                  className='shrink-0 bg-primary/70'
                  style={{
                    width: `${bins.widthPct}%`,
                    height: bin.count === 0 ? 0 : `${Math.max(12, (bin.count / bins.max) * 100)}%`
                  }}
                  title={
                    bin.count === 0
                      ? undefined
                      : `${bin.count} run${bin.count === 1 ? '' : 's'} around ` +
                        formatInZone(new Date(bin.at), viewerZone, 'ddd h:mm A')
                  }
                />
              ))}
              {nowPct >= 0 && nowPct <= 100 && (
                <div
                  className='absolute inset-y-0 w-px bg-destructive'
                  style={{ left: `${nowPct}%` }}
                />
              )}
            </div>
          </div>
        </div>

        {/* Lanes */}
        <div className='max-h-[60vh] overflow-y-auto overflow-x-hidden'>
          {lanes.map(lane => (
            <TimelineLane
              key={lane.row.schedule.id}
              lane={lane}
              start={start}
              spanMs={spanMs}
              nowPct={nowPct}
              onSelect={onSelect}
            />
          ))}
        </div>
      </div>

      {rows.length > LANE_CAP && (
        <p className='text-xs text-muted-foreground'>
          Showing {LANE_CAP} of {rows.length} schedules — narrow the filters to see the rest.
        </p>
      )}
      {totalRuns === 0 && (
        <p className='text-xs text-muted-foreground'>
          Nothing runs in this window. Try “Next 7 days”.
        </p>
      )}
    </div>
  );
};
